import {
  Col,
  type CompositionV3Ent,
  type CompositionV3Filters,
  EntKind,
  type Node,
  Op,
  POSTING_SOURCE_IDS,
  type PostingsV3DataQueryVariables,
  type PostingsV3Filters,
  type PostingsV3FiltersV2,
  PostingsV3Mode,
  type PostingsV3Req,
} from '@revelio/data-access';

import type { Filter, PrimaryFilterId } from '../types/filters';
import { FilterId, isMultiFilter } from '../types/filters';
import { roleCountsLevelFromCompositionFilters } from './composition-v3/getCompositionV3BreakdownOptions';
import { getCompositionV3Filters } from './composition-v3/getCompositionV3Filters';
import { normalizeCompositionEffectiveFilters } from './composition-v3/getCompositionV3Variables';
import { isExpressionFilter } from './filters-v2/expression-dimensions';
import { combine, filterOperands } from './filters-v2/filters-expr';
import { getV3Entities } from './get-v3-entities';
import { getV3Taxonomies } from './get-v3-taxonomies';
import { getPostingFilters } from './getPostingFilters';
import { stripEntitiesFromFilters } from './role-skill-ent-kind';

/**
 * Empty filter values (`[]` / `[[]]`) mean "no filter" but still perturb the
 * urql cache key. Drop them so the request stays byte-identical whether or not
 * a caller threads empty entries through (e.g. the landing prefetch), keeping
 * the warmed cache and the provider's request aligned.
 */
const stripEmptyFilterValues = (
  filters: PostingsV3Filters
): PostingsV3Filters =>
  Object.fromEntries(
    Object.entries(filters).filter(([, value]) => {
      if (!Array.isArray(value)) return true;
      return (
        value.length > 0 &&
        !value.every((inner) => Array.isArray(inner) && inner.length === 0)
      );
    })
  ) as PostingsV3Filters;

/**
 * Narrows a composition filter object to the subset `postings_v3` supports.
 * Postings are job ads, not people, so demographics (`gender`/`ethnicity`/
 * `highest_degree`) and free-text `keywords` are not valid postings filters
 * (the demographic fields were a backend mistake being removed) — drop them,
 * and remap `raw_title` onto the postings `title` field. Every postings
 * consumer (the data providers, the map, and the overview tooltip) routes
 * through here so they all send the same compatible set; `provider` and dates
 * are layered on by each caller.
 */
export const toPostingsCompatibleFilters = (
  filters: CompositionV3Filters
): PostingsV3Filters => {
  const {
    keywords: _keywords,
    gender: _gender,
    ethnicity: _ethnicity,
    highest_degree: _highest_degree,
    raw_title,
    ...shared
  } = filters;

  return {
    ...shared,
    ...(raw_title ? { title: raw_title } : {}),
  };
};

/**
 * Maps an entity's `EntKind` back to its primary filter dimension. The filter
 * builders need it to exclude the primary selections from `filters` (the
 * entities already cover them) and to resolve `dim1` for the Indeed provider's
 * geo/role-specific min start date.
 */
const entKindToPrimaryDimension = (
  entType: EntKind
): PrimaryFilterId | null => {
  switch (entType) {
    case EntKind.Company:
    case EntKind.RicsK10:
    case EntKind.RicsK50:
    case EntKind.RicsK400:
      return FilterId.COMPANY;
    case EntKind.Region:
    case EntKind.Country:
    case EntKind.State:
    case EntKind.Msa:
    case EntKind.City:
      return FilterId.GEOGRAPHY;
    case EntKind.RoleLvl_1:
    case EntKind.RoleLvl_2:
    case EntKind.RoleLvl_3:
    case EntKind.RoleLvl_4:
    case EntKind.RoleLvl_5:
    case EntKind.RoleLvl_6:
    case EntKind.RoleLvl_7:
      return FilterId.ROLE;
    case EntKind.SkillLvl_1:
    case EntKind.SkillLvl_2:
    case EntKind.SkillLvl_3:
    case EntKind.SkillLvl_4:
    case EntKind.SkillLvl_5:
    case EntKind.SkillLvl_6:
    case EntKind.SkillLvl_7:
    case EntKind.SkillLvl_8:
      return FilterId.CUSTOM_SKILL;
    case EntKind.ActivityLvl_1:
    case EntKind.ActivityLvl_2:
    case EntKind.ActivityLvl_3:
    case EntKind.ActivityLvl_4:
    case EntKind.ActivityLvl_5:
    case EntKind.ActivityLvl_6:
      return FilterId.ACTIVITY;
    default:
      return null;
  }
};

/**
 * Resolves the primary dimension from the requested entities. The map's geo
 * entities aren't a 1:1 copy of the primary filter (countries get enriched with
 * their MSAs), so it can't be inferred from the filter tree. Defaults to
 * GEOGRAPHY — the only entity-less caller is the map.
 */
const primaryDimensionFromEntities = (
  entities: ReadonlyArray<CompositionV3Ent>
): PrimaryFilterId =>
  (entities[0] && entKindToPrimaryDimension(entities[0].EntType)) ??
  FilterId.GEOGRAPHY;

/** Variables that send `filters`, which the schema leaves optional beside `filters_v2`. */
type PostingsV3FiltersVariables = {
  req: PostingsV3Req & { filters: PostingsV3Filters };
};

/**
 * Builds `postings_v3` variables from an EXPLICIT entity list. Used by the map
 * (its geographies-to-plot are enriched beyond the raw primary selections) and
 * as the delegate for {@link getPostingsV3Variables}. Entity ids are stripped
 * from `filters` so they live only in `req.entities` and don't double-filter.
 */
export const buildPostingsV3VariablesFromEntities = ({
  entities,
  effectiveFilters,
  isSnapshot = false,
  defaultMetricMode,
  activeFilterMap,
  primaryDimension,
}: {
  entities: CompositionV3Ent[];
  effectiveFilters: Filter[];
  isSnapshot?: boolean;
  defaultMetricMode?: 'posting' | 'expected_hires';
  activeFilterMap?: ReadonlyMap<FilterId, Filter>;
  /**
   * Explicit primary dimension; derived from entity kinds when omitted. The
   * dashboard provider and geo map pass it so enrichment and the request agree
   * on the dimension instead of relying on entity-kind derivation.
   */
  primaryDimension?: PrimaryFilterId;
}): PostingsV3FiltersVariables => {
  const normalized = normalizeCompositionEffectiveFilters(effectiveFilters);
  const resolvedPrimaryDimension =
    primaryDimension ?? primaryDimensionFromEntities(entities);

  const compositionFilters = getCompositionV3Filters({
    primaryDimension: resolvedPrimaryDimension,
    isSnapshot,
    effectiveFilters: normalized,
  });

  const filtersWithoutEntities = stripEntitiesFromFilters(
    compositionFilters,
    entities
  );

  const { role_taxonomy, skill_taxonomy, activity_taxonomy } = getV3Taxonomies(
    normalized,
    activeFilterMap
  );

  const postingFilters = getPostingFilters(
    resolvedPrimaryDimension,
    effectiveFilters,
    {
      isSnapshot,
      defaultMetricMode,
    }
  );

  // Drop the postings-incompatible filters (demographics, keywords) and remap
  // raw_title; month dates become weekly clamped dates below.
  const {
    start_date: _start_date,
    end_date: _end_date,
    ...compatibleFilters
  } = toPostingsCompatibleFilters(filtersWithoutEntities);

  const provider = (
    postingFilters.provider ?? [POSTING_SOURCE_IDS.unified]
  ).filter((id): id is number => typeof id === 'number');

  const filters = stripEmptyFilterValues({
    ...compatibleFilters,
    provider: provider.length > 0 ? provider : [POSTING_SOURCE_IDS.unified],
    start_date: postingFilters.start_date,
    end_date: postingFilters.end_date,
  });

  const mode =
    (postingFilters.metric_mode ?? defaultMetricMode) === 'expected_hires'
      ? PostingsV3Mode.Hires
      : PostingsV3Mode.Normal;

  const roles_counts_level = roleCountsLevelFromCompositionFilters(
    filtersWithoutEntities
  );

  return {
    req: {
      entities,
      filters,
      options: { mode, roles_counts_level },
      role_taxonomy,
      skill_taxonomy,
      activity_taxonomy,
    },
  };
};

/**
 * Builds `postings_v3` variables, deriving `req.entities` from the PRIMARY
 * FILTER selections, then delegating to {@link buildPostingsV3VariablesFromEntities}.
 */
export const getPostingsV3Variables = ({
  primaryDimension,
  effectiveFilters,
  isSnapshot = false,
  defaultMetricMode,
  activeFilterMap,
}: {
  primaryDimension: PrimaryFilterId;
  effectiveFilters: Filter[];
  isSnapshot?: boolean;
  defaultMetricMode?: 'posting' | 'expected_hires';
  activeFilterMap?: ReadonlyMap<FilterId, Filter>;
}): PostingsV3FiltersVariables => {
  const entities = getV3Entities({
    primaryDimension,
    filters: normalizeCompositionEffectiveFilters(effectiveFilters),
  });

  return buildPostingsV3VariablesFromEntities({
    entities,
    effectiveFilters,
    isSnapshot,
    defaultMetricMode,
    activeFilterMap,
    primaryDimension,
  });
};

const sourceLeaf = (id: number): Node => ({
  leaf: { cat: { col: Col.Provider, id } },
});

const seniorityLeaf = (id: number): Node => ({
  leaf: { cat: { col: Col.Seniority, id } },
});

const titleLeaf = (txt: string): Node => ({
  leaf: { txt: { col: Col.Title, txt } },
});

const isNode = (node: Node | null): node is Node => node !== null;

/**
 * A built request's `filters` as `filters_v2`. The page's entity filters go
 * into the expression as the user combined them, the posting sources, the
 * seniorities and the title groups as the middleware reads `filters` — any
 * value of a group, and every group — and the dates sit beside it.
 */
const toPostingsV3FiltersV2 = ({
  filters,
  primaryDimension,
  effectiveFilters,
}: {
  filters: PostingsV3Filters;
  primaryDimension: PrimaryFilterId;
  effectiveFilters: Filter[];
}): PostingsV3FiltersV2 => {
  const expressed = effectiveFilters.filter(
    (filter) =>
      (isMultiFilter(filter) ? filter.wrappedFilterId : filter.id) !==
        primaryDimension && isExpressionFilter(filter)
  );
  const sources = combine(Op.Or, (filters.provider ?? []).map(sourceLeaf));
  const seniorities = combine(
    Op.Or,
    (filters.seniority ?? []).map(seniorityLeaf)
  );
  const titles = (filters.title ?? []).map((terms) =>
    combine(Op.Or, terms.map(titleLeaf))
  );
  const filtersExpr = combine(Op.And, [
    ...filterOperands(expressed),
    ...[sources, seniorities, ...titles].filter(isNode),
  ]);

  return {
    start_date: filters.start_date,
    end_date: filters.end_date,
    ...(filtersExpr === null ? {} : { filters_expr: filtersExpr }),
  };
};

/**
 * {@link getPostingsV3Variables} for a page that filters with expressions
 * (`useFilterExpressions`), sending `filters_v2` in place of `filters`. The
 * rest of the request — dates, sources, title, the role counts' level — is
 * built from what `filters` can say: each filter's values, a multi filter's
 * instances merged, and nothing negated, which only the expression carries.
 */
export const getPostingsV3FiltersV2Variables = (
  args: Parameters<typeof getPostingsV3Variables>[0]
): PostingsV3DataQueryVariables => {
  const {
    req: { filters, ...req },
  } = getPostingsV3Variables({
    ...args,
    effectiveFilters: normalizeCompositionEffectiveFilters(
      args.effectiveFilters
    ).filter((filter) => !('isNot' in filter && filter.isNot)),
  });

  return {
    req: {
      ...req,
      filters_v2: toPostingsV3FiltersV2({
        filters,
        primaryDimension: args.primaryDimension,
        effectiveFilters: args.effectiveFilters,
      }),
    },
  };
};
